import config from "@/App/config/main";
import { RouteName } from "@/App/router/routeName";
import { openProductGuideInNewWindow } from "@/App/services/openProductGuideInNewWindow";
import { storageService, StorageKey } from "@/App/services/storage.service";
import { useCasesStore } from "@/App/stores";
import { CaseMode } from "@/App/types/CaseMode";
import { useUserStore } from "@/App/user/user.store";
import { defineStore } from "pinia";

type NavbarState = {
  color: string;
};

type NavBarGetter = {
  accessiblePages(): Page[];
  visibleLinks(): NavBarLink[];
  defaultRoute(): RouteName;
};

export const state = (): NavbarState => ({
  color: "#18159d",
});

export type NavBarLink = {
  routeName?: RouteName;
  visibleName: string;
  testId?: string;
  externalLink?: string;
};

export type Page = {
  routeName?: RouteName;
  visibleName?: string;
  testId?: string;
  accessible: boolean;
  hidden?: boolean;
  externalLink?: string;
};

interface NavBarActions {
  setColor(color: string): void;
  openProductGuide(): Promise<void>;
}

const warnNoAccessibleRoutes = (visibleLinks: NavBarLink[], accessiblePages: Page[]): void => {
  console.warn("[NAVIGATION] User has no accessible routes, redirecting to logout", {
    visibleLinks,
    accessiblePages,
  });
};

export const useNavbarStore = defineStore<string, NavbarState, NavBarGetter, NavBarActions>("navbar", {
  actions: {
    setColor(color) {
      this.color = color;
    },
    openProductGuide() {
      return openProductGuideInNewWindow();
    },
  },
  getters: {
    accessiblePages(): Page[] {
      const userStore = useUserStore();
      const casesStore = useCasesStore();
      return [
        {
          routeName: RouteName.cases,
          accessible: userStore.canSeeEdCases,
          visibleName: "Cases",
          testId: "cases-link",
        },
        {
          routeName: RouteName.edCaseDetails,
          accessible: userStore.canSeeEdCases,
        },
        {
          routeName: RouteName.ift,
          accessible: userStore.iftEnabled,
          visibleName: "IFT",
          testId: "ift",
        },
        {
          routeName: RouteName.iftCaseDetails,
          accessible: userStore.iftEnabled,
        },
        {
          routeName: RouteName.mih,
          accessible: userStore.mihEnabled,
          visibleName: "MIH",
          testId: "mih",
        },
        {
          routeName: RouteName.mihCaseDetails,
          accessible: userStore.mihEnabled,
        },
        {
          routeName: RouteName.iftReports,
          accessible: userStore.isIftReportsAvailable,
          hidden: casesStore.mode !== CaseMode.ift,
          visibleName: "Reports",
          testId: "iftreports",
        },
        {
          routeName: RouteName.mihReports,
          accessible: userStore.mihEnabled,
          hidden: casesStore.mode !== CaseMode.mih,
          visibleName: "Reports",
          testId: "mihreports",
        },
        {
          routeName: RouteName.messaging,
          accessible: userStore.canBroadcast,
          visibleName: "Messaging",
        },
        {
          routeName: RouteName.statuses,
          accessible: userStore.canManageHospitalStatuses,
          hidden: casesStore.mode !== CaseMode.ed,
          visibleName: "Statuses",
          testId: "statuses-link",
        },
        {
          routeName: RouteName.iftStatuses,
          accessible: userStore.canManageHospitalStatuses && userStore.iftEnabled,
          hidden: casesStore.mode !== CaseMode.ift,
          visibleName: "Statuses",
          testId: "ift-statuses-link",
        },
        {
          accessible: userStore.isHospitalAdmin,
          visibleName: "Admin Panel",
          externalLink: config.ADMIN_URL,
        },
        {
          routeName: RouteName.logout,
          accessible: true,
        },
      ].filter((link) => link.accessible);
    },
    visibleLinks(): NavBarLink[] {
      return this.accessiblePages.flatMap(({ hidden, routeName, externalLink, visibleName, testId }) => {
        if (!hidden && visibleName) {
          return { routeName, visibleName, testId, externalLink };
        }
        return [];
      });
    },
    defaultRoute(): RouteName {
      const defaultRouteForUser = this.visibleLinks.find((link) => link.routeName)?.routeName;
      if (!defaultRouteForUser) {
        warnNoAccessibleRoutes(this.visibleLinks, this.accessiblePages);
        storageService.save(
          StorageKey.previousSessionExpiredMessage,
          "Please contact your IT department to confirm proper account configuration.",
          false,
        );
        return RouteName.logout;
      }
      return defaultRouteForUser;
    },
  },
  state,
});
